import { trackLeadFormOpen } from "@/utils/gtm";

declare global {
  interface Window {
    InnovList?: {
      render: () => void;
    };
  }
}

let scriptLoaded = false;
let scriptLoading = false;
const pendingCallbacks: Array<(() => void) | undefined> = [];
const SCRIPT_SELECTOR = 'script[src*="forms.innovlist.com/widget.js?type=starter"]';

export const LEAD_POPUP_EVENT = "innovlist:open-lead-popup";

export const WIDGET_CONFIG = {
  src: "https://forms.innovlist.com/widget.js?type=starter",
  client: "seasoft",
  primaryColor: "#155dfc",
  primaryHover: "#1447e6",
  targetId: "my-form",
};

const getExistingWidgetScript = (): HTMLScriptElement | null => {
  if (typeof document === "undefined") return null;
  return document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);
};

export const loadWidgetScript = (callback?: () => void): void => {
  if (scriptLoaded) {
    callback && callback();
    return;
  }

  if (scriptLoading) {
    pendingCallbacks.push(callback);
    return;
  }

  scriptLoading = true;
  pendingCallbacks.push(callback);

  const existingScript = getExistingWidgetScript();
  if (existingScript) {
    scriptLoaded = true;
    scriptLoading = false;
    pendingCallbacks.forEach((cb) => cb && cb());
    pendingCallbacks.length = 0;
    return;
  }

  const script = document.createElement("script");
  script.src = WIDGET_CONFIG.src;
  script.setAttribute("data-client", WIDGET_CONFIG.client);
  script.setAttribute("data-target", WIDGET_CONFIG.targetId);
  script.setAttribute("data-primary-color", WIDGET_CONFIG.primaryColor);
  script.setAttribute("data-primary-hover", WIDGET_CONFIG.primaryHover);
  script.async = true;

  script.onload = () => {
    scriptLoaded = true;
    scriptLoading = false;
    pendingCallbacks.forEach((cb) => cb && cb());
    pendingCallbacks.length = 0;
  };

  script.onerror = () => {
    scriptLoading = false;
    console.error("Innovlist widget failed to load");
  };

  document.body.appendChild(script);
};

export const renderWidget = (): void => {
  if (typeof window !== "undefined" && window.InnovList) {
    window.InnovList.render();
  }
};

export const openLeadPopup = (context = "default"): void => {
  loadWidgetScript(() => {
    renderWidget();
  });

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(LEAD_POPUP_EVENT, {
        detail: { context },
      })
    );
  }

  trackLeadFormOpen(context);
};

// Contexts for tracking
export const FORM_CONTEXTS = {
  ENROLL_NOW: "enroll_now_button",
  JOIN_NOW: "join_now_button",
  CONTACT_PAGE: "contact_page",
  COURSE_SIDEBAR: "course_sidebar",
  COURSE_MOBILE: "course_mobile_bar",
  HERO_CTA: "hero_cta",
  COUNSELING: "free_counseling_cta",
  BLOG_CTA: "blog_post_cta",
  CARD_ENQUIRY: "home_card_enquiry",
  TESTIMONIALS: "testimonials_cta",
  FOOTER_CTA: "footer_cta",
  EXIT_INTENT: "exit_intent",
  NAV_ENROLL: "nav_enroll_button",
  LEGAL_PAGE_CTA: "legal_page_cta",
} as const;

export type FormContextType = (typeof FORM_CONTEXTS)[keyof typeof FORM_CONTEXTS];
